/*
* Copyright (C) 20019 FlexLink
* Original CPP Code Copyright (C) 2007-2016 Rafael Durbano Lobato
*
* This file is part of Recursive Partitioning Algorithm.
*
* Recursive Partitioning Algorithm is free software: you can
* redistribute it and/or modify it under the terms of the GNU General
* Public License as published by the Free Software Foundation, either
* version 3 of the License, or (at your option) any later version.
*
* Recursive Partitioning Algorithm is distributed in the hope that it
* will be useful, but WITHOUT ANY WARRANTY; without even the implied
* warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR
* PURPOSE. See the GNU General Public License for more details.
*
* You should have received a copy of the GNU General Public License
* along with Recursive Partitioning Algorithm. If not, see
* <http://www.gnu.org/licenses/>.
*
* Contact information:
*
* Rafael Durbano Lobato <lobato@ime.usp.br>
* http://www.ime.usp.br/~lobato/
*/

/* Implementation of the algorithm "Recursive Five-block Heuristic"
* described in [1] for solving the Manufacturer's Pallet Loading
* Problem.
*
* [1] E. G. Birgin, R. D. Lobato and R. Morabito. An effective
*     recursive partitioning approach for the packing of identical
*     rectangles in a rectangle. Journal of the Operational Research
*     Society (2010) 61, 306-320.
*
* Last update: July 8th, 2009
* Author:      Rafael Durbano Lobato
* Contact:     lobato@ime.usp.br
*/

import { ApContext } from './apcontext'
import {
  lIndex as utilLIndex, VERTICAL_CUT, HORIZONTAL_CUT, MEM_TYPE_4,
  B1, B2, B3, B4, B5, B6, B7, B8, B9,
  descPtoDiv2,
  descPtoDiv3, descSol,
  getKey, HOMOGENEOUS,
  normalizePiece,
  ptoDiv1,
  ptoDiv2,
  ptoDiv3, solucao,
  standardPositionB1,
  standardPositionB2,
  standardPositionB3,
  standardPositionB4,
  standardPositionB5,
  standardPositionB6,
  standardPositionB7,
  standardPositionB8,
  standardPositionB9
} from './util'
import { ptoRet, setPtoRet, drawBD } from './draw_bd'

// draw module globals
let ret = 0 // int ret

/******************************************************************
 ******************************************************************/

// inline int LIndex(int q0, int q1, int q2, int q3) {
function lIndex (q0, q1, q2, q3) {
  return utilLIndex(q0, q1, q2, q3, ApContext.memory_type)
}

/******************************************************************
 ******************************************************************/

/**
 * Calculate the lower bound of a given rectangle R(x,y) (degenerated L).
 *
 * Parameters:
 * x - Length of the rectangle.
 *
 * y - Width of the rectangle.
 *
 * Return:
 * The lower bound.
 */
// inline int R_LowerBound(int x, int y) {
function r_LowerBound (x, y) {
  x = ApContext.normalize[x]
  y = ApContext.normalize[y]
  return ApContext.lowerBound[ApContext.indexX[x]][ApContext.indexY[y]]
}

/******************************************************************
 ******************************************************************/

/**
 * Determine how to cut the L-piece.
 *
 * Parameter:
 * q - The L-piece.
 */
// short LCut(int *q) {
function lCut (q) {
  /* Divide the L-piece in two rectangles and calculate their lower
   * bounds to compose the lower bound of the L-piece. */
  // int a = R_LowerBound(q[2], q[1]) + R_LowerBound(q[0] - q[2], q[3]);
  // int b = R_LowerBound(q[2], q[1] - q[3]) + R_LowerBound(q[0], q[3]);
  const a = r_LowerBound(q[2], q[1]) + r_LowerBound(q[0] - q[2], q[3])
  const b = r_LowerBound(q[2], q[1] - q[3]) + r_LowerBound(q[0], q[3])

  return (a > b) ? VERTICAL_CUT : HORIZONTAL_CUT
}

/******************************************************************
 ******************************************************************/

/**
 * Fix the coordinates of the rectangle with the specified identifier.
 *
 * Paremeter:
 * id - Identifier of the rectangle.
 */
// void fixCoordinates(int id) {
function fixCoordinates (id) {
  if (ptoRet[id][0] > ptoRet[id][2]) {
    [ptoRet[id][0], ptoRet[id][2]] = [ptoRet[id][2], ptoRet[id][0]]
  }
  if (ptoRet[id][1] > ptoRet[id][3]) {
    [ptoRet[id][1], ptoRet[id][3]] = [ptoRet[id][3], ptoRet[id][1]]
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Normalize degenerated L-pieces.
 *
 * Parameter:
 * q - The degenerated L-piece to be normalized.
 */
// void normalizeDegeneratedL(int *q) {
function normalizeDegeneratedL (q) {
  if (q[2] === 0) {
    q[2] = q[0]
    q[1] = q[3]
  } else if (q[3] === 0) {
    q[3] = q[1]
    q[0] = q[2]
  } else if (q[2] === q[0] || q[3] === q[1]) {
    q[2] = q[0]
    q[3] = q[1]
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Shift the rectangle in the x-axis.
 *
 * Paremeters:
 * id     - Identifier of the rectangle.
 *
 * deltaX - Amount to be shifted.
 */
// void shiftX(int id, int deltaX) {
function shiftX (id, deltaX) {
  ptoRet[id][0] += deltaX
  ptoRet[id][2] += deltaX
}

/******************************************************************
 ******************************************************************/

/**
 * Shift the rectangle in the y-axis.
 *
 * Paremeters:
 * id     - Identifier of the rectangle.
 *
 * deltaY - Amount to be shifted.
 */
// void shiftY(int id, int deltaY) {
function shiftY (id, deltaY) {
  ptoRet[id][1] += deltaY
  ptoRet[id][3] += deltaY
}

/******************************************************************
 ******************************************************************/

// void P1(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p1 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    ptoRet[i][1] = q[1] - ptoRet[i][1]
    ptoRet[i][3] = q[1] - ptoRet[i][3]

    fixCoordinates(i)

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P2(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p2 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    ptoRet[i][0] = q[0] - ptoRet[i][0]
    ptoRet[i][2] = q[0] - ptoRet[i][2]

    fixCoordinates(i)

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P3(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p3 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    ptoRet[i][0] = q[0] - ptoRet[i][0]
    ptoRet[i][2] = q[0] - ptoRet[i][2]

    ptoRet[i][1] = q[1] - ptoRet[i][1]
    ptoRet[i][3] = q[1] - ptoRet[i][3]

    fixCoordinates(i)

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P4(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p4 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P5(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p5 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    const tmp1 = ptoRet[i][1] // int tmp1
    const tmp2 = ptoRet[i][3] // int tmp2

    ptoRet[i][1] = q[0] - ptoRet[i][0]
    ptoRet[i][3] = q[0] - ptoRet[i][2]

    ptoRet[i][0] = tmp1
    ptoRet[i][2] = tmp2

    fixCoordinates(i)

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P6(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p6 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    const tmp1 = ptoRet[i][0] // int tmp1
    const tmp2 = ptoRet[i][2] // int tmp2

    ptoRet[i][0] = q[1] - ptoRet[i][1]
    ptoRet[i][2] = q[1] - ptoRet[i][3]

    ptoRet[i][1] = tmp1
    ptoRet[i][3] = tmp2

    fixCoordinates(i)

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P7(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p7 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    const tmp1 = q[0] - ptoRet[i][0] // int tmp1
    const tmp2 = q[0] - ptoRet[i][2] // int tmp2

    ptoRet[i][0] = q[1] - ptoRet[i][1]
    ptoRet[i][2] = q[1] - ptoRet[i][3]

    ptoRet[i][1] = tmp1
    ptoRet[i][3] = tmp2

    fixCoordinates(i)

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

// void P8(int start, int end, int L, int *q, int deltaX, int deltaY) {
function p8 (start, end, L, q, deltaX, deltaY) {
  for (let i = start; i < end; i++) {
    const tmp1 = ptoRet[i][0] // int tmp1
    const tmp2 = ptoRet[i][2] // int tmp2

    ptoRet[i][0] = ptoRet[i][1]
    ptoRet[i][2] = ptoRet[i][3]

    ptoRet[i][1] = tmp1
    ptoRet[i][3] = tmp2

    shiftX(i, deltaX)
    shiftY(i, deltaY)
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B1 subdivision.
 */
// void drawB1(int L, int *q) {
function drawB1 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[2];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(2).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB1(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = div[1]
  if (div[0] === 0) {
    deltaY = q[3]
  }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }
  } else {
    if (width >= height) { p1(start, end, L1, q1, deltaX, deltaY) } else { p5(start, end, L1, q1, deltaX, deltaY) }
  }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = 0
  deltaY = 0
  if (div[1] === 0) { deltaX = div[0] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
  } else {
    if (width >= height) { p2(start, end, L2, q2, deltaX, deltaY) } else { p6(start, end, L2, q2, deltaX, deltaY) }
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B2 subdivision.
 */
// void drawB2(int L, int *q) {
function drawB2 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[3];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(3).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB2(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = q[3]
  if (div[1] === q[1]) { deltaX = div[0] } else if (tmp[0] === tmp[2]) { deltaY = div[1] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }
  } else {
    if (width >= height) { p3(start, end, L1, q1, deltaX, deltaY) } else { p7(start, end, L1, q1, deltaX, deltaY) }
  }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = 0
  deltaY = 0

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B3 subdivision.
 */
function drawB3 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[2];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(2).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB3(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = 0

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = div[0]
  deltaY = div[1]

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B4 subdivision.
 */
// void drawB4(int L, int *q) {
function drawB4 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[2];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(2).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB4(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = 0

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = q[3]
  deltaY = 0
  if (div[0] === q[0]) { deltaY = div[1] } else if (tmp[0] === tmp[2]) { deltaX = div[0] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
  } else {
    if (width > height) { p3(start, end, L2, q2, deltaX, deltaY) } else { p7(start, end, L2, q2, deltaX, deltaY) }
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B5 subdivision.
 */
// void drawB5(int L, int *q) {
function drawB5 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[2];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(2).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB5(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = 0
  if (div[0] === 0) { deltaY = div[1] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }
  } else {
    if (width >= height) { p1(start, end, L1, q1, deltaX, deltaY) } else { p5(start, end, L1, q1, deltaX, deltaY) }
  }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = div[0]
  deltaY = 0
  if (div[1] === 0) { deltaX = q[2] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
  } else {
    if (width >= height) { p2(start, end, L2, q2, deltaX, deltaY) } else { p6(start, end, L2, q2, deltaX, deltaY) }
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B6 subdivision.
 */
// void drawB6(int L, int *q) {
function drawB6 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[3];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(3).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
    div[2] = (ApContext.divisionPoint[L] & ptoDiv3) >> descPtoDiv3
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
    div[2] = (ApContext.divisionPointMap[L][h] & ptoDiv3) >> descPtoDiv3
  }

  standardPositionB6(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = 0
  if (div[0] === 0) { deltaY = div[1] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }
  } else {
    if (width >= height) { p1(start, end, L1, q1, deltaX, deltaY) } else { p5(start, end, L1, q1, deltaX, deltaY) }
  }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = div[0]
  deltaY = 0
  if (div[1] === 0) { deltaX = div[2] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
  } else {
    if (width >= height) { p2(start, end, L2, q2, deltaX, deltaY) } else { p6(start, end, L2, q2, deltaX, deltaY) }
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B7 subdivision.
 */
// void drawB7(int L, int *q) {
function drawB7 (L, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[3];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(3).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L] & ptoDiv2) >> descPtoDiv2
    div[2] = (ApContext.divisionPoint[L] & ptoDiv3) >> descPtoDiv3
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L][h] & ptoDiv2) >> descPtoDiv2
    div[2] = (ApContext.divisionPointMap[L][h] & ptoDiv3) >> descPtoDiv3
  }

  standardPositionB7(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = div[1]
  if (div[0] === 0) { deltaY = div[2] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }
  } else {
    if (width >= height) { p1(start, end, L1, q1, deltaX, deltaY) } else { p5(start, end, L1, q1, deltaX, deltaY) }
  }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = 0
  deltaY = 0
  if (div[1] === 0) { deltaX = div[0] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
  } else {
    if (width >= height) { p2(start, end, L2, q2, deltaX, deltaY) } else { p6(start, end, L2, q2, deltaX, deltaY) }
  }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B8 subdivision.
 */
// void drawB8(int L_index, int *q) {
function drawB8 (L_index, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[2];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(2).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L_index] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L_index] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L_index][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L_index][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB8(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = 0
  if (div[0] === 0) { deltaY = div[1] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }
  } else {
    if (width >= height) { p1(start, end, L1, q1, deltaX, deltaY) } else { p5(start, end, L1, q1, deltaX, deltaY) }
  }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = div[0]
  deltaY = 0
  if (div[1] === 0) { deltaX = div[0] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
}

/******************************************************************
 ******************************************************************/

/**
 * Draw the boxes according to the B9 subdivision.
 */
// void drawB9(int L_index, int *q) {
function drawB9 (L_index, q) {
  // int L1, L2;
  // int q1[4], q2[4], tmp[4];
  // int width, height;
  // int deltaX, deltaY;
  // int start, end;
  // int div[2];
  let L1, L2
  const q1 = new Array(4).fill(0); const q2 = new Array(4).fill(0); const tmp = new Array(4).fill(0)
  let width, height
  let deltaX, deltaY
  let start, end
  const div = new Array(2).fill(0)

  if (ApContext.memory_type === MEM_TYPE_4) {
    div[0] = ApContext.divisionPoint[L_index] & ptoDiv1
    div[1] = (ApContext.divisionPoint[L_index] & ptoDiv2) >> descPtoDiv2
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    div[0] = ApContext.divisionPointMap[L_index][h] & ptoDiv1
    div[1] = (ApContext.divisionPointMap[L_index][h] & ptoDiv2) >> descPtoDiv2
  }

  standardPositionB9(div, q, q1, q2)

  /* Draw L1. */
  tmp[0] = q1[0]
  tmp[1] = q1[1]
  tmp[2] = q1[2]
  tmp[3] = q1[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q1)
  L1 = lIndex(q1[0], q1[1], q1[2], q1[3])

  start = ret
  drawR(L1, q1)
  end = ret

  deltaX = 0
  deltaY = div[1]
  if (div[0] === 0) { deltaY = q[3] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (width >= height) { p4(start, end, L1, q1, deltaX, deltaY) } else { p8(start, end, L1, q1, deltaX, deltaY) }

  /* Draw L2. */
  tmp[0] = q2[0]
  tmp[1] = q2[1]
  tmp[2] = q2[2]
  tmp[3] = q2[3]
  normalizeDegeneratedL(tmp)

  normalizePiece(q2)
  L2 = lIndex(q2[0], q2[1], q2[2], q2[3])

  start = ret
  drawR(L2, q2)
  end = ret

  deltaX = 0
  deltaY = 0
  if (div[1] === 0) { deltaX = div[0] }

  if (tmp[0] !== tmp[1]) {
    width = tmp[0]
    height = tmp[1]
  } else {
    width = tmp[2]
    height = tmp[3]
  }

  if (tmp[0] === tmp[2]) {
    if (width >= height) { p4(start, end, L2, q2, deltaX, deltaY) } else { p8(start, end, L2, q2, deltaX, deltaY) }
  } else {
    if (width >= height) { p2(start, end, L2, q2, deltaX, deltaY) } else { p6(start, end, L2, q2, deltaX, deltaY) }
  }
}

/******************************************************************
 ******************************************************************/

// void drawR(int L, int *q) {
function drawR (L, q) {
  // int i;
  // int start, end;
  // int divisionType;
  let i
  let start, end
  let divisionType

  if (ApContext.memory_type === MEM_TYPE_4) {
    divisionType = (ApContext.solution[L] & solucao) >> descSol
  } else {
    const h = getKey(q[0], q[1], q[2], q[3], ApContext.memory_type) // int h
    divisionType = (ApContext.solutionMap[L][h] & solucao) >> descSol
  }

  // console.log('divisionType:' + divisionType)
  switch (divisionType) {
    case HOMOGENEOUS:

      /* Non-degenerated L. */
      if (q[0] !== q[2]) {
        const cut = lCut(q) // short cut

        if (cut === VERTICAL_CUT) {
          ret = drawBD(q[2], q[1], ret)
          start = ret
          ret = drawBD(ApContext.normalize[q[0] - q[2]], q[3], ret)
          end = ret
          for (i = start; i < end; i++) {
            shiftX(i, q[2])
          }
        } else {
          start = ret
          ret = drawBD(q[2], ApContext.normalize[q[1] - q[3]], ret)
          end = ret
          for (i = start; i < end; i++) {
            shiftY(i, q[3])
          }
          ret = drawBD(q[0], q[3], ret)
        }
      }
      /* Degenerated L (rectangle). */
      else {
        ret = drawBD(q[0], q[1], ret)
      }
      break

    case B1:
      drawB1(L, q)
      break
    case B2:
      drawB2(L, q)
      break
    case B3:
      drawB3(L, q)
      break
    case B4:
      drawB4(L, q)
      break
    case B5:
      drawB5(L, q)
      break
    case B6:
      drawB6(L, q)
      break
    case B7:
      drawB7(L, q)
      break
    case B8:
      drawB8(L, q)
      break
    case B9:
      drawB9(L, q)
      break
    default:
      break
  }
}

/******************************************************************
 ******************************************************************/

// void MakeFile(int Lo, int Wo, int L, int q[4], int n, const char *nativeStringSavePath) {
function makeFileContent (Lo, Wo, L, q, n) {
  // fprintf(file, "%d %d %d\n", n + 1, q[0], q[1]);
  // fprintf(file, "0 0 %d %d\n", q[0], q[1]);
  //  for(int i = 0; i < n; i++) {
  //     fprintf(file, "%d %d %d %d\n",
  //             ptoRet[i][0], ptoRet[i][1], ptoRet[i][2], ptoRet[i][3]);
  //   }
  // JS: render to string:
  // return `${n + 1} ${q[0]} ${q[1]} \n0 0 ${q[0]} ${q[1]} \n` +
  //   Array.from({length: n}, (value, key) => key)
  //     .map(i => `${ptoRet[i][0]} ${ptoRet[i][1]} ${ptoRet[i][2]} ${ptoRet[i][3]} \n`)
  //     .join('\n');
  return {
    L: q[0],
    W: q[1],
    base: [0, 0, q[0], q[1]],
    boxes: Array.from({ length: n }, (value, key) => key)
      .map(i => [ptoRet[i][0], ptoRet[i][1], ptoRet[i][2], ptoRet[i][3]])
  }
}

/******************************************************************
 ******************************************************************/

// void draw(int Lo, int Wo, int L, int q[4], int n, bool solvedWithL, const char *nativeStringSavePath) {
export function draw (Lo, Wo, L, q, n, solvedWithL) {
  // ptoRet = (int**) malloc(n * sizeof(int *));
  // for(int i = 0; i < n; i++)
  //   ptoRet[i] = (int*) malloc(4*sizeof(int));

  setPtoRet(new Array(n).fill(null).map(() => new Array(4).fill(0)))
  ret = 0
  if (solvedWithL) {
    drawR(L, q)
  } else {
    ret = drawBD(q[0], q[1], ret)
  }
  return makeFileContent(Lo, Wo, L, q, n)
}
